import { useState, useEffect, useRef } from "react";
import {
  collection,
  query,
  onSnapshot,
  orderBy,
  where,
  doc,
  Timestamp,
} from "firebase/firestore";
import { DateTime } from "luxon";
import { db, waitForAuthReady } from "../firebase";
import { tryResolveTenantId } from "../utils/tenant";

/**
 * COMMERCIAL NOTE:
 * This file uses Firestore realtime listeners. Every listener = background channel traffic.
 * Be intentional: only use realtime where you truly need it.
 */

/**
 * Employees hook
 * - Always subscribes to employees list (1 listener)
 * - Optionally subscribes to each employee's assignments (N listeners, can be expensive)
 *
 * Set listenAssignments=false for list pages where you don't need role/location detail.
 */
export function useEmployees(includeOwners = false, listenAssignments = true) {
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let mounted = true;

    let mainUnsub = null;
    const assignmentUnsubs = new Map(); // empId -> unsub

    const cleanupAssignments = () => {
      for (const unsub of assignmentUnsubs.values()) {
        try {
          if (typeof unsub === "function") unsub();
        } catch (e) {}
      }
      assignmentUnsubs.clear();
    };

    const start = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      const q = query(collection(db, `tenants/${tenantId}/employees`), orderBy("name"));

      mainUnsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;

          const allEmployees = snap.docs.map((d) => {
            const data = d.data() || {};
            return {
              id: d.id,
              ...data,
              rates: data.rates || {},
              defaultRate: Number(data.hourlyRate ?? 0),
              // keep existing assignments unless we explicitly replace them
              assignments: {},
            };
          });

          const activeEmployees = allEmployees.filter((emp) => {
            const status = emp.employment?.currentStatus;
            if (!includeOwners && emp.permissionLevel === "owner") return false;
            // your data uses "Terminated" (case sensitive) in this file
            return !status || status !== "Terminated";
          });

          // Preserve assignments already loaded for employees
          setEmployees((prev) => {
            const existingAssignments = new Map();
            prev.forEach((e) => existingAssignments.set(e.id, e.assignments || {}));
            return activeEmployees.map((e) => ({
              ...e,
              assignments: existingAssignments.get(e.id) || {},
            }));
          });

          // If we are NOT listening to assignments, ensure none are active.
          if (!listenAssignments) {
            cleanupAssignments();
            return;
          }

          // Remove assignment listeners for employees that no longer exist
          const currentIds = new Set(activeEmployees.map((e) => e.id));
          for (const [empId, unsub] of assignmentUnsubs.entries()) {
            if (!currentIds.has(empId)) {
              try {
                if (typeof unsub === "function") unsub();
              } catch (e) {}
              assignmentUnsubs.delete(empId);
            }
          }

          // Add assignment listeners for employees (one per employee)
          activeEmployees.forEach((emp) => {
            if (assignmentUnsubs.has(emp.id)) return;

            const assignmentsCol = collection(
              db,
              `tenants/${tenantId}/employees`,
              emp.id,
              "assignments"
            );

            const unsub = onSnapshot(
              assignmentsCol,
              (assignSnap) => {
                if (!mounted) return;

                const assignmentDocs = assignSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

                const nestedMap = {};
                assignmentDocs.forEach((d) => {
                  const locId = d.locationId;
                  const deptId = d.departmentId || "unknown_department";
                  const roleId = d.roleId;
                  if (!locId || !roleId) return;

                  if (!nestedMap[locId]) nestedMap[locId] = { departments: {} };
                  if (!nestedMap[locId].departments[deptId])
                    nestedMap[locId].departments[deptId] = { roles: {} };

                  nestedMap[locId].departments[deptId].roles[roleId] = {
                    assigned: typeof d.assigned === "boolean" ? d.assigned : true,
                    isPrimary: !!d.isPrimary,
                    appearsOnSchedule:
                      typeof d.appearsOnSchedule === "boolean" ? d.appearsOnSchedule : true,
                    wage: typeof d.wage !== "undefined" ? d.wage : null,
                  };
                });

                setEmployees((prev) =>
                  prev.map((e) => (e.id === emp.id ? { ...e, assignments: nestedMap } : e))
                );
              },
              (err) => {
                console.warn(`Assignment listener error for employee ${emp.id}:`, err);
              }
            );

            assignmentUnsubs.set(emp.id, unsub);
          });
        },
        (err) => {
          console.error("Employees snapshot error:", err);
          if (mounted) setEmployees([]);
        }
      );
    };

    start();

    return () => {
      mounted = false;
      try {
        if (typeof mainUnsub === "function") mainUnsub();
      } catch (e) {}
      cleanupAssignments();
    };
  }, [includeOwners, listenAssignments]);

  return employees;
}

/**
 * Shifts hook (fixed cleanup)
 */

/**
 * Helper: Generate localDateKey array for a date range
 * Used for timezone-safe shift queries
 */
function getLocalDateKeys(startDate, endDate, timeZone = 'UTC') {
  const keys = [];
  const start = DateTime.fromJSDate(startDate, { zone: timeZone });
  const end = DateTime.fromJSDate(endDate, { zone: timeZone });
  
  let current = start.startOf('day');
  while (current <= end) {
    keys.push(current.toISODate()); // YYYY-MM-DD
    current = current.plus({ days: 1 });
  }
  
  return keys;
}

export function useShifts(weekStart, locationId = null, timeZone = 'UTC') {
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    let mounted = true;
    let unsub = null;

    const start = weekStart;
    const end = new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000);

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      let q;
      
      // TIMEZONE-SAFE QUERY: Use localDateKey if locationId provided
      if (locationId) {
        const dateKeys = getLocalDateKeys(start, end, timeZone);
        q = query(
          collection(db, `tenants/${tenantId}/shifts`),
          where("locationId", "==", locationId),
          where("localDateKey", "in", dateKeys.slice(0, 10)) // Note: Can't orderBy with 'in' operator
          // Sorting will happen client-side in the component
        );
      } else {
        // LEGACY QUERY: Fallback for backward compatibility
        q = query(
          collection(db, `tenants/${tenantId}/shifts`),
          where("shiftDate", ">=", start),
          where("shiftDate", "<", end),
          orderBy("shiftDate")
        );
      }

      unsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;
          setShifts(snap.docs.map((d) => ({ id: d.id, ...d.data(), deleted: !!d.data().deleted })));
        },
        (err) => {
          console.warn("Shifts snapshot error:", err);
          if (mounted) setShifts([]);
        }
      );
    };

    run();

    return () => {
      mounted = false;
      try {
        if (typeof unsub === "function") unsub();
      } catch (e) {}
    };
  }, [weekStart, locationId, timeZone]);

  return shifts;
}

/**
 * Roles hook (fixed cleanup)
 */
export function useRoles() {
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    let mounted = true;
    let unsub = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      const q = query(collection(db, `tenants/${tenantId}/roles`), orderBy("name"));

      unsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;
          setRoles(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        },
        (err) => {
          console.warn("Roles snapshot error:", err);
          if (mounted) setRoles([]);
        }
      );
    };

    run();

    return () => {
      mounted = false;
      try {
        if (typeof unsub === "function") unsub();
      } catch (e) {}
    };
  }, []);

  return roles;
}

/**
 * Locations hook (fixed cleanup)
 * Note: this hook mutates selectedLocation if empty; you already commented about wanting a no-op setter sometimes.
 */
export function useLocations(selectedLocation, setSelectedLocation) {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    let mounted = true;
    let unsub = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      const q = query(collection(db, `tenants/${tenantId}/locations`), orderBy("name"));

      unsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;

          const list = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((l) => !l.archived);

          setLocations(list);

          if (!selectedLocation && list.length > 0) {
            // only auto-select if caller allows it (they can pass a no-op setter)
            setSelectedLocation(list[0].id);
          }
        },
        (err) => {
          console.warn("Locations snapshot error:", err);
          if (mounted) setLocations([]);
        }
      );
    };

    run();

    return () => {
      mounted = false;
      try {
        if (typeof unsub === "function") unsub();
      } catch (e) {}
    };
    // eslint-disable-next-line
  }, [selectedLocation, setSelectedLocation]);

  return locations;
}

/**
 * These three are already mostly okay because they DO create unsubMap + clearTimeout.
 * Main fix: capture the async cleanup correctly by keeping unsubMap + timeoutId in outer scope.
 */
export function useSalesProjections(weekDays) {
  const [salesProjections, setSalesProjections] = useState({});
  const [salesProjectionsInput, setSalesProjectionsInput] = useState({});
  const initializingRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const unsubMap = {};
    let timeoutId = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      initializingRef.current = true;
      setIsInitialized(false);

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      (weekDays || []).forEach((d) => {
        const key = d.toISOString().split("T")[0];
        const ref = doc(db, `tenants/${tenantId}/salesProjections`, key);

        unsubMap[key] = onSnapshot(
          ref,
          (snap) => {
            if (!mounted) return;

            const numVal = snap.exists() ? Number(snap.data().amount ?? 0) : 0;
            setSalesProjections((prev) => ({ ...prev, [key]: numVal }));

            if (initializingRef.current || !isInitialized) {
              setSalesProjectionsInput((prev) => {
                const currentVal = prev[key];
                if (currentVal === undefined || currentVal === "" || !isInitialized) {
                  return { ...prev, [key]: numVal ? String(Math.round(numVal)) : "" };
                }
                return prev;
              });
            }
          },
          () => {}
        );
      });

      timeoutId = setTimeout(() => {
        initializingRef.current = false;
        setIsInitialized(true);
      }, 500);
    };

    run();

    return () => {
      mounted = false;
      Object.values(unsubMap).forEach((unsub) => {
        try {
          if (typeof unsub === "function") unsub();
        } catch (e) {}
      });
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line
  }, [weekDays]);

  return [salesProjections, salesProjectionsInput, setSalesProjections, setSalesProjectionsInput];
}

export function useActualSales(weekDays) {
  const [actualSales, setActualSales] = useState({});
  const [actualSalesInput, setActualSalesInput] = useState({});
  const initializingRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const unsubMap = {};
    let timeoutId = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      initializingRef.current = true;
      setIsInitialized(false);

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      (weekDays || []).forEach((d) => {
        const key = d.toISOString().split("T")[0];
        const ref = doc(db, `tenants/${tenantId}/actualSales`, key);

        unsubMap[key] = onSnapshot(
          ref,
          (snap) => {
            if (!mounted) return;

            const numVal = snap.exists() ? Number(snap.data().amount ?? 0) : 0;
            setActualSales((prev) => ({ ...prev, [key]: numVal }));

            if (initializingRef.current || !isInitialized) {
              setActualSalesInput((prev) => {
                const currentVal = prev[key];
                if (currentVal === undefined || currentVal === "" || !isInitialized) {
                  return { ...prev, [key]: numVal ? String(Math.round(numVal)) : "" };
                }
                return prev;
              });
            }
          },
          () => {}
        );
      });

      timeoutId = setTimeout(() => {
        initializingRef.current = false;
        setIsInitialized(true);
      }, 500);
    };

    run();

    return () => {
      mounted = false;
      Object.values(unsubMap).forEach((unsub) => {
        try {
          if (typeof unsub === "function") unsub();
        } catch (e) {}
      });
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line
  }, [weekDays]);

  return [actualSales, actualSalesInput, setActualSales, setActualSalesInput];
}

export function useForecastedSales(weekDays) {
  const [forecastedSales, setForecastedSales] = useState({});
  const [forecastedSalesInput, setForecastedSalesInput] = useState({});
  const initializingRef = useRef(true);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    let mounted = true;
    const unsubMap = {};
    let timeoutId = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      initializingRef.current = true;
      setIsInitialized(false);

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      (weekDays || []).forEach((d) => {
        const key = d.toISOString().split("T")[0];
        const ref = doc(db, `tenants/${tenantId}/forecastedSales`, key);

        unsubMap[key] = onSnapshot(
          ref,
          (snap) => {
            if (!mounted) return;

            const numVal = snap.exists() ? Number(snap.data().amount ?? 0) : 0;
            setForecastedSales((prev) => ({ ...prev, [key]: numVal }));

            if (initializingRef.current || !isInitialized) {
              setForecastedSalesInput((prev) => {
                const currentVal = prev[key];
                if (currentVal === undefined || currentVal === "" || !isInitialized) {
                  return { ...prev, [key]: numVal ? String(Math.round(numVal)) : "" };
                }
                return prev;
              });
            }
          },
          () => {}
        );
      });

      timeoutId = setTimeout(() => {
        initializingRef.current = false;
        setIsInitialized(true);
      }, 500);
    };

    run();

    return () => {
      mounted = false;
      Object.values(unsubMap).forEach((unsub) => {
        try {
          if (typeof unsub === "function") unsub();
        } catch (e) {}
      });
      if (timeoutId) clearTimeout(timeoutId);
    };
    // eslint-disable-next-line
  }, [weekDays]);

  return [forecastedSales, forecastedSalesInput, setForecastedSales, setForecastedSalesInput];
}

/**
 * Actual Hours hook
 * FIX: capture unsub and clean it up properly.
 * NOTE: This hook depends on employees, which can be heavy if employees is subscribing to assignments.
 */
export function useActualHours(weekDays) {
  const [actualHours, setActualHours] = useState({});
  const [actualLabor, setActualLabor] = useState({});

  // IMPORTANT: If you don't need assignments for labor calc, call useEmployees(false, false)
  // so it doesn't open 1 listener per employee.
  const employees = useEmployees(false, false);

  useEffect(() => {
    let mounted = true;
    let unsub = null;

    const run = async () => {
      await waitForAuthReady();
      if (!mounted) return;

      const tenantId = await tryResolveTenantId();
      if (!tenantId || !mounted) return;

      if (!Array.isArray(weekDays) || weekDays.length === 0) {
        if (mounted) {
          setActualHours({});
          setActualLabor({});
        }
        return;
      }

      const start = new Date(weekDays[0]);
      start.setHours(0, 0, 0, 0);

      const end = new Date(weekDays[weekDays.length - 1]);
      end.setDate(end.getDate() + 1);
      end.setHours(0, 0, 0, 0);

      const q = query(
        collection(db, `tenants/${tenantId}/timeEntries`),
        where("timestamp", ">=", Timestamp.fromDate(start)),
        where("timestamp", "<", Timestamp.fromDate(end)),
        orderBy("timestamp")
      );

      unsub = onSnapshot(
        q,
        (snap) => {
          if (!mounted) return;

          const entries = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((e) => e.status !== "voided");

          const byDate = {};
          entries.forEach((e) => {
            const ts = e.timestamp && e.timestamp.toDate ? e.timestamp.toDate() : new Date(e.timestamp);
            const key = ts.toISOString().split("T")[0];
            if (!byDate[key]) byDate[key] = {};
            const emp = e.employeeId || "unknown";
            if (!byDate[key][emp]) byDate[key][emp] = [];
            byDate[key][emp].push(e);
          });

          const result = {};
          const laborResult = {};

          const calculateHours = (cinDate, coutDate) => {
            if (!cinDate || !coutDate) return 0;
            const diffMs = coutDate - cinDate;
            const diffHours = diffMs / (1000 * 60 * 60);
            return Math.round(diffHours * 100) / 100;
          };

          Object.keys(byDate).forEach((dateKey) => {
            let dayTotal = 0;
            let dayLabor = 0;

            Object.entries(byDate[dateKey]).forEach(([empId, entriesForEmp]) => {
              entriesForEmp.sort((a, b) => {
                const ta = a.timestamp && a.timestamp.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
                const tb = b.timestamp && b.timestamp.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
                return ta - tb;
              });

              const clockIns = entriesForEmp.filter((x) => x.type === "clock_in");
              const clockOuts = entriesForEmp.filter((x) => x.type === "clock_out");

              for (let i = 0; i < clockIns.length; i++) {
                const cin = clockIns[i];
                const cinDate = cin.timestamp && cin.timestamp.toDate ? cin.timestamp.toDate() : new Date(cin.timestamp);

                const cout = clockOuts.find((o) => {
                  const od = o.timestamp && o.timestamp.toDate ? o.timestamp.toDate() : new Date(o.timestamp);
                  return od > cinDate;
                });

                const coutDate = cout ? (cout.timestamp && cout.timestamp.toDate ? cout.timestamp.toDate() : new Date(cout.timestamp)) : null;

                let hrs = calculateHours(cinDate, coutDate);

                const breakMinutes = (cout && cout.breakMinutes) || (cin && cin.breakMinutes) || 0;
                if (breakMinutes) hrs = Math.max(0, hrs - breakMinutes / 60);

                dayTotal += hrs;

                const roleId = (cout && cout.roleId) || (cin && cin.roleId) || null;
                const emp = employees.find((e) => e.id === empId) || {};
                let rate = Number(emp.defaultRate ?? 0) || 0;

                if (emp.rates && roleId && emp.rates[roleId] !== undefined) {
                  rate = Number(emp.rates[roleId]);
                }

                const labor = Math.round(hrs * rate * 100) / 100;
                dayLabor += labor;
              }
            });

            result[dateKey] = Math.round(dayTotal * 100) / 100;
            laborResult[dateKey] = Math.round(dayLabor * 100) / 100;
          });

          weekDays.forEach((d) => {
            const k = d.toISOString().split("T")[0];
            if (result[k] === undefined) result[k] = 0;
            if (laborResult[k] === undefined) laborResult[k] = 0;
          });

          setActualHours(result);
          setActualLabor(laborResult);
        },
        () => {
          const zeros = {};
          const zerosLabor = {};
          (weekDays || []).forEach((d) => {
            zeros[d.toISOString().split("T")[0]] = 0;
            zerosLabor[d.toISOString().split("T")[0]] = 0;
          });
          if (mounted) {
            setActualHours(zeros);
            setActualLabor(zerosLabor);
          }
        }
      );
    };

    run();

    return () => {
      mounted = false;
      try {
        if (typeof unsub === "function") unsub();
      } catch (e) {}
    };
    // eslint-disable-next-line
  }, [JSON.stringify(weekDays)]);

  return { hours: actualHours, labor: actualLabor };
}
